import axios from "axios";
import { getRouter, getStore } from "./runtimeDeps";
import launchDarklyService from "../services/launchdarkly";
import configService from "../services/config";
import * as Sentry from "@sentry/vue";
import { resetInMemoryFilterState } from "@/utils/resetInMemoryFilterState";
import { maybePersistAccountDetailFromAxiosResponse } from "@/utils/accountProductFlags";
import {
  MODE_PREVIEW,
  MODE_VIEW_AS,
  VIEW_AS_ADMIN_RESTORE_KEY,
  VIEW_AS_EMPLOYEES_RETURN_PATH,
  getImpersonationSession,
  restoreAdminContextFromStash,
} from "@components/utils/impersonationSession";

/**
 * Whether a failed request should replace the page with the app-wide 404 view.
 *
 * Optional/background requests can opt out with `config.skipNotFoundRouteError`
 * so a missing resource only hides that widget instead of breaking the page
 * (ER-12272 AC-14, Organization health card).
 *
 * @param {Object} error - Axios error object
 * @returns {boolean}
 */
export const shouldShowNotFoundPage = (error) => {
  const response = error && error.response ? error.response : null;
  const data = response ? response.data : undefined;
  return Boolean(
    response?.status === 404 &&
      error?.config?.skipNotFoundRouteError !== true &&
      data &&
      Object.prototype.hasOwnProperty.call(data, "detail") &&
      data.detail === "Not found."
  );
};

/**
 * Create Axios Instance for API Call
 *
 * @param {boolean} withToken
 * @param { { headers: Object, baseURL: string } } options
 */
export const createAxiosInstance = (withToken = true, options = {}) => {
  // const { headers = {}, baseURL = `${process.env.VUE_APP_BASE_URL}` } = options;
  const { headers = {}, baseURL } = options;

  const instanceConfig = {
    baseURL: baseURL,
    headers: {
      "Content-Type": "application/json",
      ...(withToken ? { Authorization: "Token " + localStorage.getItem("token") } : {}),
      ...headers,
    },
  };

  const axiosInstance = axios.create(instanceConfig);
  axiosInstance.interceptors.response.use(
    (response) => {
      maybePersistAccountDetailFromAxiosResponse(response);
      return response;
    },
    (error) => {
      const response = error && error.response ? error.response : null;
      const status = response ? response.status : undefined;
      const data = response ? response.data : undefined;
      if (status === 401) {
        // Stale request after token rotation (Exit restored admin while an
        // in-flight view-as/preview call still 401'd). Ignoring this is what
        // stops DEV from bouncing Exit → /login when localhost looks fine.
        const requestAuth =
          error?.config?.headers?.Authorization || error?.config?.headers?.authorization || "";
        const requestToken = String(requestAuth)
          .replace(/^Token\s+/i, "")
          .trim();
        const currentToken = localStorage.getItem("token") || "";
        if (currentToken && requestToken && requestToken !== currentToken) {
          return Promise.reject(error);
        }

        // ER-10317: during view-as/preview a 401 (expired read-only token, or stop
        // endpoint) must restore the admin stash — not wipe to /login.
        const session = getImpersonationSession();
        const hasViewAsStash =
          typeof sessionStorage !== "undefined" &&
          !!sessionStorage.getItem(VIEW_AS_ADMIN_RESTORE_KEY);
        const isInAccountReadOnly =
          hasViewAsStash ||
          session?.readOnly === true ||
          session?.mode === MODE_VIEW_AS ||
          session?.mode === MODE_PREVIEW;

        if (isInAccountReadOnly) {
          const restored = restoreAdminContextFromStash();
          if (restored) {
            window.location.assign(VIEW_AS_EMPLOYEES_RETURN_PATH);
            return Promise.reject(error);
          }
        }

        Sentry.setUser(null);
        configService.clearSecureConfig();
        // ER-10191: clear in-memory filter/view singletons before session wipe
        resetInMemoryFilterState();
        localStorage.clear();
        launchDarklyService.resetToAnonymous();

        const router = getRouter();
        if (router && router.currentRoute && router.currentRoute.path != "/login") {
          router.push({
            path: "/login",
          });
        }
      }
      // Read-only view-as/preview: surface a clear toast instead of a generic error.
      if (
        status === 403 &&
        data &&
        (data.code === "IMPERSONATION_READ_ONLY" || data.detail === "IMPERSONATION_READ_ONLY")
      ) {
        window.dispatchEvent(
          new CustomEvent("impersonation-read-only", {
            detail: {
              message: "You're in a read-only session — this action is unavailable.",
            },
          })
        );
      }
      // Handle 404 Not Found errors (e.g., redirect to a custom 404 page).
      // Optional/background requests can set `skipNotFoundRouteError` so a
      // missing resource hides that widget instead of replacing the page.
      if (shouldShowNotFoundPage(error)) {
        const store = getStore();
        if (store) {
          store.dispatch("routeErrorHandling/setError", {
            code: 404,
            message: "Bad request. Please check your input.",
          });
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export default createAxiosInstance;
